/**
 * Shared letterhead branding for exported reports (Excel + PDF).
 *
 * Single source of truth for company identity in exports — reads the same
 * src/config/company.ts used by the login page and business cards, so a
 * change there (name, address, logo) automatically propagates to every
 * report export without touching the export code itself.
 */
import { companyConfig } from "#/config/company";
import logoUrl from "#/assets/eatta-logo.png";

export type ReportLetterhead = {
  companyName: string;
  addressLine: string;
  contactLine: string;
};

export function getReportLetterhead(): ReportLetterhead {
  return {
    companyName: companyConfig.name,
    addressLine: companyConfig.location,
    contactLine: [companyConfig.officePhone, companyConfig.email, companyConfig.website]
      .filter(Boolean)
      .join("  ·  "),
  };
}

/** "Generated by <user> on <date>" stamp — pass the current user's display name/email in from
 *  the caller (a plain lib file can't use the useAuth() hook directly). Falls back to just the
 *  timestamp when no user is available. */
export function getGeneratedStamp(generatedBy?: string | null): string {
  const when = new Date().toLocaleString("en-KE", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  return generatedBy ? `Generated by ${generatedBy} · ${when}` : `Generated ${when}`;
}

// Logo bytes are fetched once and cached — every export in a session reuses the same buffer
// instead of re-fetching the asset per download.
let logoBufferPromise: Promise<ArrayBuffer> | null = null;

export function getLogoArrayBuffer(): Promise<ArrayBuffer> {
  if (!logoBufferPromise) {
    logoBufferPromise = fetch(logoUrl).then((r) => r.arrayBuffer());
  }
  return logoBufferPromise;
}

// jsPDF wants a data URL (or base64 string), not a raw ArrayBuffer.
let logoDataUrlPromise: Promise<string> | null = null;

export function getLogoDataUrl(): Promise<string> {
  if (!logoDataUrlPromise) {
    logoDataUrlPromise = getLogoArrayBuffer().then((buf) => {
      const bytes = new Uint8Array(buf);
      let binary = "";
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return `data:image/png;base64,${btoa(binary)}`;
    });
  }
  return logoDataUrlPromise;
}

// Real pixel size of src/assets/eatta-logo.png — used to keep the aspect ratio correct
// wherever it's placed (PDF mm sizing, Excel column/row sizing).
export const LOGO_ASPECT = 343 / 339;
